import { ApiService } from '@supply-mover-2-0/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import { UserSelector } from '../../components/forms/entity-selectors/UserSelector';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';

/** All available client bulk actions */
const BULK_ACTION_OPTIONS = [
	{ value: 'assign-sales-rep', label: 'Assign Sales Rep' },
	{ value: 'unassign-sales-rep', label: 'Unassign Sales Rep' },
	{ value: 'assign-alternate-rep', label: 'Assign Alternate Rep' },
	{ value: 'unassign-alternate-rep', label: 'Unassign Alternate Rep' },
	{ value: 'assign-to-group', label: 'Assign to Group' },
	{ value: 'remove-from-group', label: 'Remove from Group' },
	{ value: 'change-lifecycle-stage', label: 'Change Lifecycle Stage' },
	{ value: 'add-to-list', label: 'Add to List' },
	{ value: 'distribute-leads', label: 'Distribute Leads' },
	{ value: 'delete-clients', label: 'Delete Clients' },
	{ value: 'undelete-clients', label: 'Recover Clients' },
];

/** Lead/Customer filter options */
const LEAD_OR_CUSTOMER_OPTIONS = [
	{ value: '', label: 'All' },
	{ value: 'Lead', label: 'Lead' },
	{ value: 'Customer', label: 'Customer' },
];

/** Sales status filter options */
const SALES_STATUS_OPTIONS = [
	{ value: '', label: 'All' },
	{ value: 'Active', label: 'Active' },
	{ value: 'Inactive', label: 'Inactive' },
];

/** Call status filter options */
const CALL_STATUS_OPTIONS = [
	{ value: '', label: 'All' },
	{ value: 'Callable', label: 'Callable' },
	{ value: 'Do Not Call', label: 'Do Not Call' },
];

interface ClientBulkActionStepConfigProps {
	config: Record<string, unknown>;
	setConfig: (config: Record<string, unknown>) => void;
}

/**
 * Configuration UI for the Client Bulk Action automation step.
 * Allows users to select a bulk action type, configure filter criteria
 * to target specific clients, and set action-specific parameters.
 */
export function ClientBulkActionStepConfig({
	config,
	setConfig,
}: ClientBulkActionStepConfigProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const actionType = config['actionType'] as string | undefined;
	const filters = (config['filters'] ?? {}) as Record<
		string,
		Array<{ id: string | number; label: string }>
	>;
	const searchTerm = (config['searchTerm'] as string) ?? '';
	const actionParams = (config['actionParams'] ?? {}) as Record<
		string,
		unknown
	>;

	// Data for selectors
	const [groups, setGroups] = useState<Array<{ id: number; name: string }>>([]);
	const [lifecycleStages, setLifecycleStages] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [clientLists, setClientLists] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [salespeople, setSalespeople] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [loadingData, setLoadingData] = useState(true);

	// Load reference data on mount
	useEffect(() => {
		const loadData = async (): Promise<void> => {
			try {
				const [groupsRes, stagesRes, listsRes, salesRes] = await Promise.all([
					ApiService.apiGet<{
						data: Array<{ id: number; name: string }>;
					}>('/groups'),
					ApiService.apiGet<{
						data: Array<{ id: number; name: string }>;
					}>('/client-lifecycle-stages'),
					ApiService.apiGet<{
						success: boolean;
						data: Array<{ id: number; name: string }>;
					}>('/clients/client-lists'),
					ApiService.apiGet<
						| {
								data: Array<{
									salesperson_id: number;
									display_name: string;
								}>;
						  }
						| Array<{
								salesperson_id: number;
								display_name: string;
						  }>
					>('/users/salespeople'),
				]);

				setGroups(groupsRes?.data ?? []);
				setLifecycleStages(stagesRes?.data ?? []);
				setClientLists(listsRes?.data ?? []);

				// Salespeople endpoint can return either wrapped or raw array
				const salesData = Array.isArray(salesRes)
					? salesRes
					: (salesRes?.data ?? []);
				setSalespeople(
					salesData.map(
						(s: { salesperson_id: number; display_name: string }) => ({
							id: s.salesperson_id,
							name: s.display_name,
						}),
					),
				);
			} catch {
				console.debug('Failed to load reference data for bulk action config');
			} finally {
				setLoadingData(false);
			}
		};

		void loadData();
	}, []);

	// Helper to update a specific filter
	const updateFilter = useCallback(
		(
			filterKey: string,
			value: Array<{ id: string | number; label: string }> | null,
		): void => {
			const newFilters = { ...filters };

			if (value == null || value.length === 0) {
				delete newFilters[filterKey];
			} else {
				newFilters[filterKey] = value;
			}

			setConfig({ ...config, filters: newFilters });
		},
		[config, filters, setConfig],
	);

	// Helper to update action params
	const updateActionParam = useCallback(
		(key: string, value: unknown): void => {
			setConfig({
				...config,
				actionParams: { ...actionParams, [key]: value },
			});
		},
		[config, actionParams, setConfig],
	);

	// Handle action type change - clear action params when switching
	const handleActionTypeChange = useCallback(
		(value: string): void => {
			setConfig({
				...config,
				actionType: value,
				actionParams: {},
			});
		},
		[config, setConfig],
	);

	// Get currently selected filter value for a single-select radio-style filter
	const getRadioFilterValue = useCallback(
		(filterKey: string): string => {
			const filterValue = filters[filterKey];
			const firstValue = filterValue?.[0];

			if (firstValue == null) {
				return '';
			}

			return String(firstValue.id);
		},
		[filters],
	);

	// Set a radio-style filter (single value)
	const setRadioFilter = useCallback(
		(filterKey: string, value: string, label: string): void => {
			if (value === '') {
				updateFilter(filterKey, null);
			} else {
				updateFilter(filterKey, [{ id: value, label }]);
			}
		},
		[updateFilter],
	);

	// Check if an item is selected in a multi-select filter
	const isMultiSelectItemSelected = useCallback(
		(filterKey: string, itemId: number): boolean => {
			const filterValue = filters[filterKey];

			if (filterValue == null) {
				return false;
			}

			return filterValue.some((item) => Number(item.id) === itemId);
		},
		[filters],
	);

	// Toggle item in multi-select filter
	const toggleMultiSelectItem = useCallback(
		(filterKey: string, itemId: number, itemLabel: string): void => {
			const currentItems = filters[filterKey] ?? [];
			const isSelected = currentItems.some(
				(item) => Number(item.id) === itemId,
			);

			if (isSelected) {
				const newItems = currentItems.filter(
					(item) => Number(item.id) !== itemId,
				);
				updateFilter(filterKey, newItems.length > 0 ? newItems : null);
			} else {
				updateFilter(filterKey, [
					...currentItems,
					{ id: itemId, label: itemLabel },
				]);
			}
		},
		[filters, updateFilter],
	);

	// Count active filters
	const activeFilterCount = useMemo(() => {
		let count = Object.keys(filters).length;

		if (searchTerm.trim().length > 0) {
			count++;
		}

		return count;
	}, [filters, searchTerm]);

	return (
		<div className="space-y-6">
			{/* Section 1: Action Type */}
			<div>
				<label
					className="block text-sm font-semibold mb-2"
					style={{ color: colors.values.textPrimary }}
				>
					{t('Bulk Action')}
				</label>
				<Select
					options={BULK_ACTION_OPTIONS.map((opt) => ({
						value: opt.value,
						label: t(opt.label),
					}))}
					value={(actionType as string) ?? ''}
					onChange={handleActionTypeChange}
					placeholder={t('Select a bulk action...')}
				/>
			</div>

			{/* Section 2: Filter Criteria */}
			<div>
				<label
					className="block text-sm font-semibold mb-2"
					style={{ color: colors.values.textPrimary }}
				>
					{t('Filter Criteria')}
					{activeFilterCount > 0 && (
						<span
							className={cn(
								'ml-2 text-xs font-normal px-2 py-0.5 rounded-full',
								colors.classes.badgeInfo,
							)}
						>
							{activeFilterCount} {t('active')}
						</span>
					)}
				</label>
				<p
					className="text-xs mb-3"
					style={{ color: colors.values.textSecondary }}
				>
					{t(
						'Configure which clients the bulk action will apply to. Leave empty to match all clients.',
					)}
				</p>

				<div className="space-y-3">
					{/* Search Term */}
					<FilterField label={t('Search')}>
						<Input
							value={searchTerm}
							onChange={(e) => {
								setConfig({
									...config,
									searchTerm: e.target.value,
								});
							}}
							placeholder={t('Search by name, address, email, phone...')}
							size="sm"
						/>
					</FilterField>

					{/* Lead or Customer */}
					<FilterField label={t('Client Type')}>
						<Select
							options={LEAD_OR_CUSTOMER_OPTIONS.map((opt) => ({
								value: opt.value,
								label: t(opt.label),
							}))}
							value={getRadioFilterValue('leadOrCustomer')}
							onChange={(value) => {
								const opt = LEAD_OR_CUSTOMER_OPTIONS.find(
									(o) => o.value === value,
								);
								setRadioFilter('leadOrCustomer', value, opt?.label ?? value);
							}}
							size="sm"
						/>
					</FilterField>

					{/* Sales Status */}
					<FilterField label={t('Sales Status')}>
						<Select
							options={SALES_STATUS_OPTIONS.map((opt) => ({
								value: opt.value,
								label: t(opt.label),
							}))}
							value={getRadioFilterValue('salesStatus')}
							onChange={(value) => {
								const opt = SALES_STATUS_OPTIONS.find((o) => o.value === value);
								setRadioFilter('salesStatus', value, opt?.label ?? value);
							}}
							size="sm"
						/>
					</FilterField>

					{/* Call Status */}
					<FilterField label={t('Call Status')}>
						<Select
							options={CALL_STATUS_OPTIONS.map((opt) => ({
								value: opt.value,
								label: t(opt.label),
							}))}
							value={getRadioFilterValue('callStatus')}
							onChange={(value) => {
								const opt = CALL_STATUS_OPTIONS.find((o) => o.value === value);
								setRadioFilter('callStatus', value, opt?.label ?? value);
							}}
							size="sm"
						/>
					</FilterField>

					{/* Assigned Salesperson */}
					<FilterField label={t('Assigned Salesperson')}>
						<MultiSelectList
							items={[{ id: -1, name: 'Unassigned' }, ...salespeople]}
							selectedIds={
								filters['salespeople']?.map((f) => Number(f.id)) ?? []
							}
							onToggle={(id, name) => {
								// Use string 'null' for unassigned to match filter convention
								const filterId = id === -1 ? 'null' : id;
								const filterLabel = id === -1 ? 'Unassigned' : name;
								const currentItems = filters['salespeople'] ?? [];
								const isSelected = currentItems.some(
									(item) =>
										(id === -1 && item.id === 'null') || Number(item.id) === id,
								);

								if (isSelected) {
									const newItems = currentItems.filter(
										(item) =>
											!(
												(id === -1 && item.id === 'null') ||
												Number(item.id) === id
											),
									);
									updateFilter(
										'salespeople',
										newItems.length > 0 ? newItems : null,
									);
								} else {
									updateFilter('salespeople', [
										...currentItems,
										{
											id: filterId,
											label: filterLabel,
										},
									]);
								}
							}}
							loading={loadingData}
							emptyMessage={t('No salespeople available')}
						/>
					</FilterField>

					{/* Lifecycle Stage */}
					<FilterField label={t('Lifecycle Stage')}>
						<MultiSelectList
							items={lifecycleStages}
							selectedIds={
								filters['lifecycleStageName']?.map((f) => Number(f.id)) ?? []
							}
							onToggle={(id, name) => {
								// lifecycleStageName filter uses name as id
								const currentItems = filters['lifecycleStageName'] ?? [];
								const isSelected = currentItems.some(
									(item) => item.id === name,
								);

								if (isSelected) {
									const newItems = currentItems.filter(
										(item) => item.id !== name,
									);
									updateFilter(
										'lifecycleStageName',
										newItems.length > 0 ? newItems : null,
									);
								} else {
									updateFilter('lifecycleStageName', [
										...currentItems,
										{ id: name, label: name },
									]);
								}
							}}
							loading={loadingData}
							emptyMessage={t('No lifecycle stages available')}
						/>
					</FilterField>

					{/* Groups */}
					<FilterField label={t('Groups')}>
						<MultiSelectList
							items={groups}
							selectedIds={filters['groups']?.map((f) => Number(f.id)) ?? []}
							onToggle={(id, name) => {
								toggleMultiSelectItem('groups', id, name);
							}}
							loading={loadingData}
							emptyMessage={t('No groups available')}
						/>
					</FilterField>

					{/* Client Lists */}
					<FilterField label={t('Client Lists')}>
						<MultiSelectList
							items={clientLists}
							selectedIds={
								filters['clientLists']?.map((f) => Number(f.id)) ?? []
							}
							onToggle={(id, name) => {
								toggleMultiSelectItem('clientLists', id, name);
							}}
							loading={loadingData}
							emptyMessage={t('No client lists available')}
						/>
					</FilterField>
				</div>
			</div>

			{/* Section 3: Action Parameters */}
			{actionType != null && (
				<div>
					<label
						className="block text-sm font-semibold mb-2"
						style={{ color: colors.values.textPrimary }}
					>
						{t('Action Parameters')}
					</label>
					<ActionParamsSection
						actionType={actionType}
						actionParams={actionParams}
						updateActionParam={updateActionParam}
						groups={groups}
						lifecycleStages={lifecycleStages}
						clientLists={clientLists}
						loadingData={loadingData}
					/>
				</div>
			)}
		</div>
	);
}

// ============================================
// Sub-components
// ============================================

/** Wrapper for a labeled filter field */
function FilterField({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}): React.ReactElement {
	const colors = useThemeColors();

	return (
		<div>
			<label
				className="block text-xs font-medium mb-1"
				style={{ color: colors.values.textSecondary }}
			>
				{label}
			</label>
			{children}
		</div>
	);
}

/** Reusable multi-select list with checkboxes */
function MultiSelectList({
	items,
	selectedIds,
	onToggle,
	loading,
	emptyMessage,
}: {
	items: Array<{ id: number; name: string }>;
	selectedIds: Array<number>;
	onToggle: (id: number, name: string) => void;
	loading: boolean;
	emptyMessage: string;
}): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	if (loading) {
		return (
			<div
				className="text-xs py-2"
				style={{ color: colors.values.textSecondary }}
			>
				{t('Loading...')}
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<div
				className="text-xs py-2"
				style={{ color: colors.values.textSecondary }}
			>
				{emptyMessage}
			</div>
		);
	}

	return (
		<div
			className="max-h-32 overflow-y-auto border rounded text-xs"
			style={{ borderColor: colors.values.borderMedium }}
		>
			{items.map((item) => {
				const isSelected = selectedIds.includes(item.id);

				return (
					<button
						key={item.id}
						type="button"
						onClick={() => {
							onToggle(item.id, item.name);
						}}
						className={cn(
							'w-full px-2 py-1.5 text-left flex items-center gap-2',
							'hover:opacity-80',
							isSelected
								? colors.classes.controlSelected
								: colors.classes.textPrimary,
						)}
					>
						<input
							type="checkbox"
							checked={isSelected}
							readOnly
							className="pointer-events-none"
						/>
						<span>{t(item.name)}</span>
					</button>
				);
			})}
		</div>
	);
}

/** Renders action-specific parameter inputs based on the selected action type */
function ActionParamsSection({
	actionType,
	actionParams,
	updateActionParam,
	groups,
	lifecycleStages,
	clientLists,
	loadingData,
}: {
	actionType: string;
	actionParams: Record<string, unknown>;
	updateActionParam: (key: string, value: unknown) => void;
	groups: Array<{ id: number; name: string }>;
	lifecycleStages: Array<{ id: number; name: string }>;
	clientLists: Array<{ id: number; name: string }>;
	loadingData: boolean;
}): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	switch (actionType) {
		case 'assign-sales-rep':
			return (
				<div>
					<label
						className="block text-xs font-medium mb-1"
						style={{ color: colors.values.textSecondary }}
					>
						{t('Sales Rep to Assign')}
					</label>
					<UserSelector
						value={(actionParams['salesRepId'] as number | null) ?? null}
						onChange={(value) => {
							updateActionParam('salesRepId', value);
						}}
						placeholder={t('Search for a sales rep...')}
					/>
				</div>
			);

		case 'assign-alternate-rep':
			return (
				<div>
					<label
						className="block text-xs font-medium mb-1"
						style={{ color: colors.values.textSecondary }}
					>
						{t('Alternate Rep to Assign')}
					</label>
					<UserSelector
						value={(actionParams['alternateRepId'] as number | null) ?? null}
						onChange={(value) => {
							updateActionParam('alternateRepId', value);
						}}
						placeholder={t('Search for a sales rep...')}
					/>
				</div>
			);

		case 'assign-to-group':
		case 'remove-from-group':
			return (
				<div>
					<label
						className="block text-xs font-medium mb-1"
						style={{ color: colors.values.textSecondary }}
					>
						{t(
							actionType === 'assign-to-group'
								? 'Group to Assign'
								: 'Group to Remove From',
						)}
					</label>
					<Select
						options={groups.map((g) => ({
							value: g.id,
							label: t(g.name),
						}))}
						value={
							actionParams['groupId'] != null
								? String(actionParams['groupId'])
								: ''
						}
						onChange={(value) => {
							updateActionParam('groupId', value !== '' ? Number(value) : null);
						}}
						placeholder={t('Select a group...')}
						isLoading={loadingData}
						size="sm"
					/>
				</div>
			);

		case 'change-lifecycle-stage':
			return (
				<div>
					<label
						className="block text-xs font-medium mb-1"
						style={{ color: colors.values.textSecondary }}
					>
						{t('Lifecycle Stage')}
					</label>
					<Select
						options={[
							{
								value: 'unassign',
								label: t('Unassign Stage'),
							},
							...lifecycleStages.map((s) => ({
								value: s.id,
								label: t(s.name),
							})),
						]}
						value={
							actionParams['stageId'] != null
								? String(actionParams['stageId'])
								: ''
						}
						onChange={(value) => {
							updateActionParam(
								'stageId',
								value === 'unassign'
									? null
									: value !== ''
										? Number(value)
										: null,
							);
						}}
						placeholder={t('Select a lifecycle stage...')}
						isLoading={loadingData}
						size="sm"
					/>
				</div>
			);

		case 'add-to-list':
			return (
				<div>
					<label
						className="block text-xs font-medium mb-1"
						style={{ color: colors.values.textSecondary }}
					>
						{t('Client List')}
					</label>
					<Select
						options={clientLists.map((l) => ({
							value: l.id,
							label: t(l.name),
						}))}
						value={
							actionParams['listId'] != null
								? String(actionParams['listId'])
								: ''
						}
						onChange={(value) => {
							updateActionParam('listId', value !== '' ? Number(value) : null);
						}}
						placeholder={t('Select a client list...')}
						isLoading={loadingData}
						size="sm"
					/>
				</div>
			);

		case 'distribute-leads':
			return (
				<div>
					<p className="text-xs" style={{ color: colors.values.textSecondary }}>
						{t(
							'Leads will be distributed among eligible sales reps based on their lead eligibility rules and capacity.',
						)}
					</p>
				</div>
			);

		case 'unassign-sales-rep':
		case 'unassign-alternate-rep':
			return (
				<p className="text-xs" style={{ color: colors.values.textSecondary }}>
					{t(
						'No additional parameters required. The selected rep assignment will be removed from all matched clients.',
					)}
				</p>
			);

		case 'delete-clients':
			return (
				<p
					className="text-xs font-medium"
					style={{ color: colors.values.danger }}
				>
					{t(
						'Warning: This will soft-delete all matched clients. Externally-managed clients will be skipped.',
					)}
				</p>
			);

		case 'undelete-clients':
			return (
				<p className="text-xs" style={{ color: colors.values.textSecondary }}>
					{t('This will recover all matched soft-deleted clients.')}
				</p>
			);

		default:
			return (
				<p className="text-xs" style={{ color: colors.values.textSecondary }}>
					{t('Select a bulk action to configure parameters.')}
				</p>
			);
	}
}
